import { action, computed, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import { DbKey, WithDbId, makeWithDbId } from '../../../../../adl-gen/common/db';
import {
  LearningProvider,
  LearningProviderCatalogEntry,
  makeTrainingOffering,
  Skill,
  SkillTrainingOffering,
  TrainingOffering,
  TrainingOfferingCategory
} from '../../../../../adl-gen/totalinteraction/futurefit/db';
import { RepositoryEntries, TrainingOfferingData } from '../../../../../adl-gen/totalinteraction/futurefit/frontend';
import { Route } from '../../../../../routing/app-routes';
import { Modal } from '../../../../../ui/components/modal/modal';
import { Tile } from '../../../../../ui/components/tile/tile';
import { MarkdownContent } from '../../../../../ui/widgets/markdown-content/markdown-content';
import { SelectOption } from '../../../../../ui/widgets/select/select';
import { newNonEmptyTextField, newRegexTextField, newTextField } from '../../../../../ui/widgets/text-input/text-field';
import * as FileUtility from '../../../../../util/file-repos-utility';
import { SkillTrainingOfferingChange, TrainingOfferingStore } from '../../../../stores';
import {
  deleteHandler,
  MarkdownEditorObserver,
  renderButtons,
  SelectObserver,
  TextInputObserver
} from '../../../common/editor';
import { AdminApplicationRoleType } from '../../../common/list/list-types';
import { ImageFileManager, SelectedFileSpec } from '../../image-file-manager/image-file-manager';
import { TrainingOfferingListEntry } from '../../lists/training-offering-lists/training-offering-list-view';
import { TabContainer } from '../../tab-container/tab-container';
import { SkillTrainingOfferingListEntry } from '../../lists/training-offering-lists/skill-training-offering-list-view';
import {
  TrainingOfferingToSkillListView,
  UnassignedSkillListView
} from '../../lists/training-offering-lists/training-offering-to-skill-list-view';
import { ListView } from '../../../common/list/list-view';
import { makePaginated } from '../../../../../adl-gen/common';
import { SkillListEntry } from '../../lists/skill-lists/skill-list-view';
import { uniqueId } from '../../../../../util/utils';

const styles = require('../../../common/editor.css');

interface TrainingOfferingEditorProps {
  debug?: boolean;
  item?: TrainingOfferingListEntry;
  data: TrainingOfferingData;
  store: TrainingOfferingStore;
  onCreate(item: TrainingOffering): Promise<void>;
  onUpdate?(item: WithDbId<TrainingOffering>, updates: SkillTrainingOfferingChange[]): Promise<void>;
  onCancel(): void;
  applicationRole: AdminApplicationRoleType;
  navigateTo(route: Route): void;
}

/**
 * Component to maintain Training Offerings.
 */
@observer
export class TrainingOfferingEditor extends React.Component<TrainingOfferingEditorProps> {
  @observable fieldState = {
    title: newRegexTextField(/^.{1,99}$/, 'Title must be less than 100 characters'),
    duration: newTextField(),
    executeUrl: newRegexTextField(
      /// This is a URL in the database but we should allow for upload as well
      /// FIXME this regex is not the best, a better one would be /^(http[s]?:(\/){2})([^\s\/]+\/).*$/ but
      /// grouping () does not seem to work
      /^http[s]?:\/\/[^\s\/]+\/?.*$/,
      'Only valid URLs allowed'
    ),

    tileUrl: newRegexTextField(/^http[s]?:\/\/[^\s\/]+\/?.*$/, 'Only valid URLs allowed'),
    description: newTextField(),
    /// default to elearning...
    category: TrainingOfferingCategory.elearning,

    providerId: newNonEmptyTextField()
  };

  @observable assignedSkills: Array<SkillTrainingOfferingChange> = [];

  /// New tile URL
  @observable newTile?: SelectedFileSpec;

  /// Learning Provider Catalog Entry
  @observable catalogEntryId: DbKey<LearningProviderCatalogEntry> | null = null;

  /// Not used for update nav - but for Preview of offering when opened
  @observable isOpen: boolean = false;

  /// ----- [ TEMP until S3 sync is done ] -----

  /// Can't use "file://../../../assets/images/skillseeker-logo.jpg" - "Not allowed to load local resource"
  readonly defaultImg =
    'https://com-hx-futurefit-uat-resources.s3-ap-southeast-2.amazonaws.com/data-images/recommendations/tia/tiles/elearning-discovery.png';

  @observable locked = false;

  @observable openDialog = false;

  constructor(props: Readonly<TrainingOfferingEditorProps>) {
    super(props);
    const state = this.fieldState;
    if (props.item) {
      const { item } = props;
      state.title.onChange(item.title);
      state.duration.onChange(item.duration);
      state.executeUrl.onChange(item.executeUrl);
      state.tileUrl.onChange(item.tileUrl);
      state.description.onChange(item.description);
      state.category = item.category;
      state.providerId.onChange(item.providerId);
      this.catalogEntryId = item.catalogEntryId;
      this.assignedSkills = props.data.availableSkills
        // Pick up only the STOs associated with the training offering
        .filter(sto => sto.value.trainingOfferingId === item.id)
        .map(sto => ({
          kind: 'loaded',
          id: sto.id,
          skillId: sto.value.skillId,
          trainingOfferingId: item.id,
          method: sto.value.method
        }));
    } else {
      this.fieldState.tileUrl.onChange(this.defaultImg);
      this.fieldState.description.onChange(this.setDefaultDescriptionTemplate());
    }
  }

  @action toggleOpen = () => {
    this.isOpen = !this.isOpen;
  };

  @action lockUpdates = () => (this.locked = true);

  @action unlockUpdates = () => (this.locked = false);

  categoryOptions = (): SelectOption[] => {
    return [
      {
        label: 'Self-Paced',
        value: this.categoryToString(TrainingOfferingCategory.elearning)
      },
      {
        label: 'Virtual',
        value: this.categoryToString(TrainingOfferingCategory.virtual)
      },
      {
        label: 'Face to Face',
        value: this.categoryToString(TrainingOfferingCategory.face2face)
      }
    ];
  };

  categoryToString = (cat: TrainingOfferingCategory): string => {
    switch (cat) {
      case TrainingOfferingCategory.elearning:
        return 'Self-Paced';
      case TrainingOfferingCategory.face2face:
        return 'Face to Face';
      case TrainingOfferingCategory.virtual:
        return 'Virtual';
    }
  };

  categoryToOption = (cat: TrainingOfferingCategory): SelectOption => {
    switch (cat) {
      case TrainingOfferingCategory.elearning:
        return { label: 'Self-Paced', value: 'elearning' };
      case TrainingOfferingCategory.face2face:
        return { label: 'Face to Face', value: 'face2face' };
      case TrainingOfferingCategory.virtual:
        return { label: 'Virtual', value: 'virtual' };
    }
  };

  optionToCategory = (option: SelectOption): TrainingOfferingCategory => {
    switch (option.value) {
      case 'elearning':
        return TrainingOfferingCategory.elearning;
      case 'face2face':
        return TrainingOfferingCategory.face2face;
      case 'virtual':
      default:
        return TrainingOfferingCategory.virtual;
    }
  };

  setDefaultDescriptionTemplate = () => {
    console.log('DEBUG setDefaultTemplate call, checkDescriptionField', this.checkDescriptionField);
    const buttonText = (this.currentImageUri.includes('mailto:'))
      ? 'Register interest'
      : 'Launch';
    return `
![{{TrainingOffering.title}}](${this.currentImageUri})

<button>[${buttonText}]({{TrainingOffering.executeUrl}})</button>

*Enter Description here*

### COURSE OUTCOMES

### COURSE CONTENT

### TARGET AUDIENCE

### DURATION
{{TrainingOffering.duration}}

### DELIVERY

### LEARNING PROVIDER

  `;
  };

  @computed
  get checkDescriptionField() {
    if (this.props.item === undefined && 
      (this.fieldState.description.getValid() === undefined)) {
        return true;
       }
    return false;
  }


  @action
  onSetCategory = (value: SelectOption) => {
    this.fieldState.category = this.optionToCategory(value);
  };

  @computed
  get providerOptions(): SelectOption[] {
    const nullOption = { label: '<Select from List>', value: '' };
    const providerOptions = this.props.data.learningProviders.reduce((acc: SelectOption[], provider) => {
      if (!acc.find(p => p.value === provider.id))
        acc.push({ label: provider.value.name, value: provider.id });
      return acc;
    },[]);
    return [nullOption, ...providerOptions];
  }

  selectedProviderOption = (providerId: DbKey<LearningProvider>): SelectOption => {
    const option = this.providerOptions.find(opt => opt.value === providerId);
    return option !== undefined ? option : { label: 'None', value: '' };
  };

  @action
  onSetProvider = (provider: SelectOption) => {
    this.fieldState.providerId.set(`${provider.value}`);
  };

  providerToString = (providerId: DbKey<LearningProvider>) => {
    const provider = this.props.data.learningProviders.find(s => s.id === providerId);
    return provider ? provider.value.name : 'None';
  };

  @computed
  get catalogOptions(): SelectOption[] {
    const nullOption = { label: 'None', value: '' };
    const providerId = this.fieldState.providerId;
    if (providerId.getValid() !== undefined) {
      const providerCatalogOptions = this.props.data.catalog
        .filter(entry => entry.value.providerId === providerId.value())
        .map(entry => ({ label: entry.value.name, value: entry.id }));
      return [nullOption, ...providerCatalogOptions];
    }
    return [nullOption];
  }

  selectedCatalogOption = (catalogEntryId: DbKey<LearningProviderCatalogEntry> | null): SelectOption => {
    const option = this.catalogOptions.find(opt => opt.value === catalogEntryId);
    return option !== undefined ? option : { label: 'None', value: '' };
  };

  @action
  onSetCatalogEntry = (catalogEntry: SelectOption) => {
    this.catalogEntryId = catalogEntry.value === '' ? null : `${catalogEntry.value}`;
  };

  onTileUrlSelect = async (newFile: SelectedFileSpec) => {
    switch (newFile.kind) {
      // ignore "default"
      case 'default':
        return;
      // should not happen - no FileSelector
      case 'repositoryFile':
        this.fieldState.tileUrl.onChange(newFile.uri);
        break;
      // Not implemented
      case 'remoteFile':
        this.fieldState.tileUrl.onChange(newFile.uri);
        break;
      case 'upload': {
        // Leave to onSave to handle
        runInAction(() => this.newTile = newFile);
      }
    }
  };

  getRepo = (repoType: 'tiles' | 'content'): RepositoryEntries | undefined => {
    return FileUtility.getRecommendationsRepo(repoType, this.fieldState.providerId.value());
  };

  uploadToRepo = async (newFile: SelectedFileSpec): Promise<string | undefined> => {
    const result = await FileUtility.uploadRepoFile(newFile, this.props.store.uploadRepositoryEntry);
    return result;
  };

  TitleObserver = observer(() =>
    TextInputObserver({
      field: this.fieldState.title,
      type: 'text',
      title: 'Title of Training Offering',
      placeholder: '',
      xsSpan: 4,
      isMandatory: true
    })
  );

  DurationObserver = observer(() =>
    TextInputObserver({
      field: this.fieldState.duration,
      type: 'text',
      title: 'Duration',
      placeholder: 'eg. 30 minutes or 2 days',
      xsSpan: 2,
    })
  );

  @computed
  get currentImageUri() {
    // First check if there's a new tile uploaded
    return this.newTile ? this.newTile.uri :
      // If not, check the current URL
      this.fieldState.tileUrl.getValid() !== undefined ? this.fieldState.tileUrl.value() : 
      // If all else fails
      this.defaultImg;
  }

  TileUrlObserver = observer(() => {
    return (
      <ImageFileManager
        title={'Image URL'}
        repositoryEntries={this.getRepo('tiles')}
        defaultUrl={this.currentImageUri}
        onChange={this.onTileUrlSelect}
        hideFileSelector={true}
        hidePath={true}
        isMandatory={true}
      />
    );
  });

  ExecuteUrlObserver = observer(() =>
    TextInputObserver({
      field: this.fieldState.executeUrl,
      type: 'text',
      title: 'Target URL (Offering, information page or enrolment "mailto:" URL)',
      placeholder: 'https://example.com/coursename',
      xsSpan: 4,
      isMandatory: true,
    })
  );

  DescriptionObserver = observer(() => {
    const repo = this.getRepo('content');
    if (repo !== undefined) {
      return MarkdownEditorObserver({
        title: 'Description',
        field: this.fieldState.description,
        repo,
        onUpload: this.props.store.uploadRepositoryEntry,
        xsSpan: 4
      });
    } else {
      return null;
    }
  });

  CategoryObserver = observer(() =>
    SelectObserver({
      options: this.categoryOptions(),
      selectedOption: this.categoryToOption(this.fieldState.category),
      title: 'Category',
      xsSpan: 2,
      onChange: this.onSetCategory,
      isMandatory: true,
    })
  );

  ProviderObserver = observer(() =>
    SelectObserver({
      options: this.providerOptions,
      selectedOption: this.selectedProviderOption(this.fieldState.providerId.value()),
      title: 'Learning Provider',
      xsSpan: 4,
      onChange: this.onSetProvider,
      isMandatory: true,
    })
  );

  CatalogEntryObserver = observer(() =>
    SelectObserver({
      options: this.catalogOptions,
      selectedOption: this.selectedCatalogOption(this.catalogEntryId),
      title: 'Learning Provider Catalog Entry (if any)',
      xsSpan: 4,
      onChange: this.onSetCatalogEntry,
    })
  );

  @computed
  get currentUnassignedSkills(): SkillListEntry[] {
    const { data, applicationRole } = this.props;
    return (
      data.skills
        .map(sk => ({ ...sk.value, id: sk.id, identity: applicationRole }))
        // Remove skill already assigned
        .filter(sk => !this.assignedSkills.map(sto => (sto.kind !== 'delete' ? sto.skillId : undefined)).includes(sk.id))
    );
  }

  // Doesn't actually remove from unassigned list (doesn't exist), but just adds a 'create' to assigned list
  @action
  onRemoveSkillFromUnassignedList = ({ id }: { id: DbKey<Skill> }) => {
    if (!this.props.item) return;
    const stoAlreadyExists = this.assignedSkills.findIndex(
      sto => (sto.kind !== 'delete' && sto.skillId === id)
    );
    if (stoAlreadyExists < 0) {
      this.props.item &&
        this.assignedSkills.push({
          kind: 'create',
          id: `${uniqueId()}`,
          skillId: id,
          trainingOfferingId: this.props.item.id,
          method: { kind: 'relevance', value: 0 }
        });
    }
  };

  @action
  onDeleteSkillTrainingOffering = ({ id }: { id: DbKey<SkillTrainingOffering> }) => {
    // Display is already updated in the ListView, but let's log the delete.
    const stoToDelete = this.assignedSkills.findIndex(
      sto => (sto.kind !== 'delete' && sto.id === id)
    );
    if (stoToDelete >= 0) {
      // Note this automatically adds to unassigned
      if (this.assignedSkills[stoToDelete].kind === 'create') 
        // Artifact item - just delete
        this.assignedSkills.splice(stoToDelete, 1);
      else
        // Item loaded from database: delete, then add back as a 'delete'
        this.assignedSkills.splice(stoToDelete, 1, { kind: 'delete', id });
    }
  };

  @action
  onUpdateSkillTrainingOffering = (fieldName: string, item: SkillTrainingOfferingListEntry) => (
    value: string | number
  ) => {
    const stoToUpdate = this.assignedSkills.findIndex(
      sto => (sto.kind !== 'delete') && sto.id === item.id
    );
    if (stoToUpdate >= 0 && fieldName === 'method') {
      this.assignedSkills.splice(stoToUpdate, 1, {
        // If it's a 'create', must remain as a CREATE or we'll try to write nonsense into the db
        kind: this.assignedSkills[stoToUpdate].kind === 'create' ? 'create' : 'update',
        id: item.id,
        skillId: item.skillId,
        trainingOfferingId: item.trainingOfferingId,
        method: { kind: 'relevance', value: value as number }
      });
    }
  };

  onSelectSkill = async (id: DbKey<Skill>) => {
    // Drastic  - flips you straight out of training offerings and into the skill page, dropping all changes
    // Todo - don't navigate, just pull skill view into <dialog> component (maybe the skill preview card?)
    this.props.navigateTo({
      route: 'admin',
      page: 'skill',
      id
    });
  }

  AssignedSkillTrainingOfferingsObserver = observer(() => {
    // Don't display for 'new' - we need a T/O ID and don't want to orchestrate that rn
    if (!this.props.item) {
      return null;
    }
    const { data } = this.props;
    const assignedWithoutDeletes = this.assignedSkills.filter(sk => sk.kind !== 'delete');
    return (assignedWithoutDeletes.length > 0 ?
      <>
        <h5>Assigned Skills and Relevance Values</h5>
        <ListView
          // Trigger proper re-render on change
          key={`${assignedWithoutDeletes.length}`}
          list={makePaginated({
            items: assignedWithoutDeletes,
            current_offset: 0,
            total_size: assignedWithoutDeletes.length
          })}
          fields={TrainingOfferingToSkillListView}
          refData={data}
          onDelete={this.onDeleteSkillTrainingOffering}
          onChange={this.onUpdateSkillTrainingOffering}
          onSelect={this.onSelectSkill}
        />
      </>
      : null
    );
  });

  UnassignedSkillsObserver = observer(() => {
    // Don't display for 'new' - we need a T/O ID and don't want to orchestrate that rn
    if (!this.props.item) {
      return null;
    }
    return (
      <>
        <h5>Unassigned Skills</h5>
        <ListView
          // Trigger proper re-render on change
          key={`${this.currentUnassignedSkills.length}`}
          list={makePaginated({
            items: this.currentUnassignedSkills,
            current_offset: 0,
            total_size: this.currentUnassignedSkills.length
          })}
          fields={UnassignedSkillListView}
          onDelete={this.onRemoveSkillFromUnassignedList} // ... and add 'create' to assigned list
          onSelect={this.onSelectSkill}
        />
      </>
    );
  });

  @computed
  get inputIsValid(): boolean {
    return (
      this.fieldState.title.getValid() !== undefined &&
      this.fieldState.duration.getValid() !== undefined &&
      this.fieldState.executeUrl.getValid() !== undefined &&
      this.fieldState.tileUrl.getValid() !== undefined &&
      this.fieldState.description.getValid() !== undefined &&
      this.fieldState.providerId.getValid() !== undefined &&
      this.assignedSkills.every(sto =>
        sto.kind !== 'delete' ? sto.method.kind === 'relevance' && sto.method.value >= 1 : true
      )
    );
  }

  inputIsNonEmpty = () => {
    /// title and providerId are mandatory fields, and category cannot be undefined
    /// => just test for valid input, not for empty string
    return (
      this.inputIsValid &&
      /// These are the only two that should really be set
      (this.fieldState.executeUrl.value() !== '' || this.fieldState.tileUrl.value() !== '')
    );
  };

  onSave = async () => {
    const { item, onCreate, onUpdate } = this.props;
    runInAction(() => this.lockUpdates());

    // Look for uploaded files
    if (this.newTile) {
      const newTileUrl = await this.uploadToRepo(this.newTile);
      if (newTileUrl) {
        this.fieldState.tileUrl.onChange(newTileUrl);
      }
    }

    /// Build from scratch here to get latest changes
    const rec = makeTrainingOffering({
      title: this.fieldState.title.value(),
      duration: this.fieldState.duration.value(),
      executeUrl: this.fieldState.executeUrl.value(),
      tileUrl: this.fieldState.tileUrl.value(),
      description: this.fieldState.description.value(),
      category: this.fieldState.category,
      providerId: this.fieldState.providerId.value(),
      catalogEntryId: this.catalogEntryId,
      imageUrls: []
    });

    if (item && onUpdate) {
      await onUpdate(
        makeWithDbId({
          id: item.id,
          value: { ...rec }
        }),
        this.assignedSkills
      );
    } else {
      await onCreate(rec);
    }

    runInAction(() => this.unlockUpdates());
  };

  Edit = () => (
    <div className={styles.page}>
      <div className={styles.body}>
        <div className={styles.row}>
          <h4>{this.props.item ? 'Update' : 'New'} Training Offering</h4>
        </div>
        <div className={styles.row}>
          <this.TitleObserver />
        </div>
        <div className={styles.row}>
          <this.DurationObserver />
          <this.CategoryObserver />
        </div>
        <div className={styles.row}>
          <this.ProviderObserver />
        </div>
        <div className={styles.row}>
          <this.CatalogEntryObserver />
        </div>
        <div className={styles.row}>
          <this.ExecuteUrlObserver />
        </div>
        <div className={styles.row}>
          <this.TileUrlObserver />
        </div>
        <div className={styles.row}>
          <this.AssignedSkillTrainingOfferingsObserver />
        </div>
        <div className={styles.row}>{' '}
        </div>
        <div className={styles.row}>
          <this.UnassignedSkillsObserver />
        </div>
      </div>
    </div>
  );

  Preview = observer(() => (
    <React.Fragment>
      <this.Detail />
      <Tile
        kind={'recommendation'}
        onClick={this.toggleOpen}
        imageUrl={this.currentImageUri}
        title={this.fieldState.title.value()}
        subtitle={this.categoryToString(this.fieldState.category)}
        enabled={true}
        completed={false}
      />
    </React.Fragment>
  ));

  Detail = observer(() => {
    return (
      <Modal
        isOpen={this.isOpen}
        onRequestClose={this.toggleOpen}
        shouldCloseOnOverlayClick={true}
        shouldCloseOnEsc={true}
      >
        <div className={styles.title}>{this.fieldState.title.value()}</div>
        <div className={styles.description}>
          <MarkdownContent text={this.fieldState.description.value()} />
        </div>
      </Modal>
    );
  });

  render() {
    const { item, onUpdate } = this.props;
    const title = (item && onUpdate ? 'Update' : 'New') + ' Training Offering';
    return (
      <React.Fragment>
        {/* TODO aria (or DOM) labels */}
        <TabContainer
          items={[
            {
              key: 0,
              title,
              content: <this.Edit />,
              selected: true
            },
            {
              key: 1,
              title: 'Description',
              content: <this.DescriptionObserver />,
              selected: false,
              disabled: this.fieldState.providerId.getValid() === undefined && (this.fieldState.executeUrl.getValid() === undefined || this.fieldState.executeUrl.getValid() === 'empty')
            },
            {
              key: 2,
              title: 'Preview',
              content: <this.Preview />,
              selected: false,
              disabled: !this.inputIsNonEmpty()
            }
          ]}
          selectedTabKey={0}
          nonTabContent={renderButtons(
            this.onSave,
            undefined,
            this.props.onCancel,
            () => this.inputIsValid && !this.locked,
            undefined,
            () => !this.locked
          )}
        />
      </React.Fragment>
    );
  }
}

///
/// Should be disabled in production
///
interface TrainingOfferingDeleterProps {
  items?: WithDbId<TrainingOffering>[];
  onDelete?(ids: DbKey<TrainingOffering>[]): Promise<void>;
  onCancel(): void;
}

/** Base component to delete AppClient */
// @observer
export class TrainingOfferingDeleter extends React.Component<TrainingOfferingDeleterProps> {
  onDelete = async () => {
    const { items, onDelete } = this.props;
    if (items && onDelete) {
      await onDelete(items.map(item => item.id));
    }
  };

  render() {
    const { onCancel, items } = this.props;
    return items ? (
      <div className={styles.page}>
        <div className={styles.body}>
          <div className={styles.row}>
            {deleteHandler(this.onDelete, onCancel, 'Training Offerings', items.map(item => item.value.title))}
          </div>
        </div>
      </div>
    ) : null;
  }
}



// WEBPACK FOOTER //
// ./src/admin/ui/components/editors/training-offering-editor/training-offering-editor.tsx